import { HelpTypePreview } from '@/nodes/HelpTypePreview';
import { InfoTypeDisplay, NodeBody, NodeContainer, NodeErrorIcon, NodeHeader, NodePrompts } from '@/nodes/NodeUI';
import type { YesNoNodeType } from '@/nodes/types';
import type { GuideNodeId } from '@rock/types';
import { outgoingHandles } from '@rock/types';
import { Handle, type NodeProps, Position } from '@xyflow/react';
import { type FC, useContext } from 'react';
import { FlowParamsContext } from '../app/FlowInitializer';

const YesNoNode: FC<NodeProps<YesNoNodeType>> = ({ id, selected, data }) => {
    const paths = outgoingHandles['yes-no'](id as GuideNodeId, data);
    const { spaceId } = useContext(FlowParamsContext);

    const yes = (
        <div key="yes" className={`border-t-1 border-gray-300 py-2 bg-white text-xs relative`}>
            {data.yesLabel}
            <Handle type="source" id={paths.yes} position={Position.Right} />
        </div>
    );

    const no = (
        <div key="no" className={`border-t-1 border-gray-300 py-2 bg-white text-xs relative`}>
            {data.noLabel}
            <Handle type="source" id={paths.no} position={Position.Right} />
        </div>
    );

    const unsure = data.forceSelection ? undefined : (
        <div key="unsure" className={`border-t-1 border-gray-300 py-2 bg-white text-xs relative`}>
            Not Sure
            <Handle type="source" id={paths.unsure} position={Position.Right} />
        </div>
    );

    return (
        <NodeContainer>
            <NodeErrorIcon id={id} />
            <NodeHeader id={id} type={data.type} title={data.pageTitle} />
            <NodeBody selected={selected}>
                <div className="relative">
                    <NodePrompts prompts={data.prompts} />
                    <Handle type="target" position={Position.Left} />
                </div>

                {data.infoTypeId && (
                    <div className="flex flex-col items-center">
                        <InfoTypeDisplay spaceId={spaceId} typeId={data.infoTypeId} />
                    </div>
                )}

                <div className="flex flex-col items-end px-3">
                    <HelpTypePreview
                        helpType={data.helpType}
                        spaceId={spaceId}
                        modelHelpTypeId={data.equipmentTypeId}
                    />
                </div>

                <div className="py-2">
                    {data.noFirst ? [no, yes, unsure] : [yes, no, unsure]}
                    <div className={`border-t-1 border-gray-300`} />
                </div>
            </NodeBody>
        </NodeContainer>
    );
};

export default YesNoNode;
